import {
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  Box,
  Heading,
  Center,
  Spinner,
  Stack,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useParams, Link as ReactRouterLink } from "react-router-dom";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useApiClient } from "../../hooks/useApiClient";
import { CherryPayApi } from "../../api/models";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Card } from "../../components/Card/Card";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import DateUtil from "../../util/DateUtil";
import { CardOrderOrderStatusLozenge } from "../../components/CardOrderSummary/CardOrderOrderStatusLozenge";

export const CardOrderDetail = () => {
  const business = useBusinessContext();
  const [orderDetail, setOrderDetail] =
    useState<CherryPayApi.CardOrderResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const params = useParams();
  const apiClient = useApiClient();

  const getCardOrderDetail = async (
    businessId: string,
    memberId: string,
    orderId: string
  ) => {
    setIsLoading(true);
    setErrorMessage(undefined);
    const result = await apiClient.getCardOrderDetail(
      businessId,
      memberId,
      orderId
    );
    if (result.ok) {
      setOrderDetail(result.data);
    } else {
      setErrorMessage(
        result.statusCode === 404
          ? "Card order not found or member not found."
          : result.message
      );
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (params.cardOrderMemberId && params.orderId) {
      getCardOrderDetail(
        business.BusinessId,
        params.cardOrderMemberId,
        params.orderId
      );
    }
  }, [params.cardOrderMemberId, params.orderId]);

  if (isLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={ReactRouterLink}
              to={`/businesses/${business.BusinessId}/card-orders`}
            >
              Card Orders
            </BreadcrumbLink>
          </BreadcrumbItem>
          {orderDetail?.MemberDetails && (
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink
                as={ReactRouterLink}
                to={`/businesses/${business.BusinessId}/card-orders/${params.cardOrderMemberId}/${params.orderId}`}
              >
                {orderDetail.MemberDetails.FirstName}{" "}
                {orderDetail.MemberDetails.LastName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
        </BusinessBreadcrumbs>
        <PageHeading.Title>View Card Order</PageHeading.Title>
      </PageHeading>

      <PageContent>
        {errorMessage && !orderDetail && (
          <Card w="100%">
            <Box p={8}>
              <ErrorMessage>{errorMessage}</ErrorMessage>
              <Text mt={4}>
                The card order could not be loaded. This may be because:
              </Text>
              <Box as="ul" mt={2} ml={6}>
                <li>The order ID is invalid</li>
                <li>The member ID is invalid</li>
                <li>You don't have permission to view this order</li>
              </Box>
            </Box>
          </Card>
        )}
        {errorMessage && orderDetail && (
          <ErrorMessage mb={4}>{errorMessage}</ErrorMessage>
        )}
        {orderDetail && (
          <Card w="100%">
            <Stack
              w="100%"
              alignItems="start"
              spacing={{ base: 4, xl: 24 }}
              bgColor="gray.600"
              py={5}
              px={8}
              borderTopStartRadius="3xl"
              borderTopEndRadius="3xl"
              direction={{ base: "column", xl: "row" }}
              boxShadow="md"
            >
              <VStack spacing="1" alignItems="flex-start">
                <Text fontSize="2xl" color="white" fontWeight="bold">
                  {orderDetail.OrderId?.substring(0, 8)}
                </Text>
                <CardOrderOrderStatusLozenge
                  orderStatus={orderDetail.OrderStatus}
                />
              </VStack>

              <VStack
                spacing="1"
                alignSelf={{ base: "flex-start", xl: "flex-end" }}
                alignItems={{ base: "flex-start", xl: "center" }}
              >
                <Text fontSize="2xl" color="white" fontWeight="bold">
                  {orderDetail.MemberDetails?.FirstName}{" "}
                  {orderDetail.MemberDetails?.LastName}
                </Text>
                <Box
                  sx={{
                    display: "inline-block",
                    height: "20px",
                    lineHeight: "20px",
                  }}
                >
                  <Text variant="small-label">Card Holder</Text>
                </Box>
              </VStack>
            </Stack>

            <Box
              boxShadow="md"
              borderRadius="lg"
              px={8}
              pt={8}
              pb={10}
              bgColor="white"
              w="100%"
            >
              <Stack
                w="100%"
                align="start"
                spacing={2}
                direction={["column", "row"]}
              >
                <VStack align="start" w="100%" flexGrow={1}>
                  <Heading variant="view-heading">Order Information</Heading>
                  <VStack align="start" w="100%" spacing={6} pb={10}>
                    <ViewControl
                      label="Order ID:"
                      value={
                        orderDetail.OrderId
                          ? `${orderDetail.OrderId.substring(0, 8)} (${
                              orderDetail.OrderId
                            })`
                          : "-"
                      }
                    />

                    <ViewControl
                      label="Order Status:"
                      value={orderDetail.OrderStatus ?? "-"}
                    />

                    <ViewControl
                      label="Order Started Date:"
                      value={
                        orderDetail.OrderDate
                          ? DateUtil.getLocalDateFormatFromString(
                              orderDetail.OrderDate
                            )
                          : "-"
                      }
                    />

                    <ViewControl
                      label="Last Change:"
                      value={
                        orderDetail.LastChange?.Item1 &&
                        orderDetail.LastChange?.Item2
                          ? `${
                              orderDetail.LastChange.Item1
                            } ${DateUtil.getLocalDateFormatFromString(
                              orderDetail.LastChange.Item2
                            )}`
                          : "-"
                      }
                    />

                    <ViewControl
                      label="Ordered By:"
                      value={orderDetail.OrderedByUser ?? "-"}
                    />

                    <ViewControl
                      label="Initial Card Value:"
                      value={
                        orderDetail.InitialCardValue != null
                          ? `$${orderDetail.InitialCardValue.toFixed(2)}`
                          : "-"
                      }
                    />
                  </VStack>
                </VStack>

                <VStack align="start" w="100%" flexGrow={1}>
                  <Heading variant="view-heading">Member Details</Heading>
                  <VStack align="start" w="100%" spacing={6} pb={10}>
                    <ViewControl label="Member:">
                      <Box flex={3}>
                        {orderDetail.MemberId && !errorMessage && (
                          <PreviewButton
                            url={`/businesses/${business.BusinessId}/members/${orderDetail.MemberId}`}
                            linkProps={{ target: "_self" }}
                            hover={true}
                            adjustMargin
                          >
                            <Text variant="view-value">View Member</Text>
                          </PreviewButton>
                        )}
                        {orderDetail.MemberId && errorMessage && (
                          <Text variant="view-value" color="red.500">
                            Member not found (ID: {orderDetail.MemberId})
                          </Text>
                        )}
                        {!orderDetail.MemberId && (
                          <Text variant="view-value">-</Text>
                        )}
                      </Box>
                    </ViewControl>

                    <ViewControl
                      label="Membership Number:"
                      value={orderDetail.MemberDetails?.MembershipNumber ?? "-"}
                    />

                    <ViewControl
                      label="First Name:"
                      value={orderDetail.MemberDetails?.FirstName ?? "-"}
                    />

                    <ViewControl
                      label="Last Name:"
                      value={orderDetail.MemberDetails?.LastName ?? "-"}
                    />

                    <ViewControl
                      label="Email Address:"
                      value={orderDetail.MemberDetails?.EmailAddress ?? "-"}
                    />

                    <ViewControl
                      label="Mobile Number:"
                      value={orderDetail.MemberDetails?.MobileNumber ?? "-"}
                    />
                  </VStack>
                </VStack>
              </Stack>

              {orderDetail.ConfirmedPostalAddress && (
                <Stack
                  w="100%"
                  align="start"
                  spacing={2}
                  direction={["column", "row"]}
                  pt={4}
                  borderTop="1px solid"
                  borderColor="gray.200"
                >
                  <VStack align="start" w="100%" flexGrow={1}>
                    <Heading variant="view-heading">Delivery Address</Heading>
                    <VStack align="start" w="100%" spacing={6} pb={10}>
                      <ViewControl
                        label="Address Line 1:"
                        value={
                          orderDetail.ConfirmedPostalAddress?.AddressLine1 ??
                          "-"
                        }
                      />

                      {orderDetail.ConfirmedPostalAddress?.AddressLine2 && (
                        <ViewControl
                          label="Address Line 2:"
                          value={
                            orderDetail.ConfirmedPostalAddress.AddressLine2
                          }
                        />
                      )}

                      {orderDetail.ConfirmedPostalAddress?.AddressLine3 && (
                        <ViewControl
                          label="Address Line 3:"
                          value={
                            orderDetail.ConfirmedPostalAddress.AddressLine3
                          }
                        />
                      )}

                      <ViewControl
                        label="Suburb:"
                        value={
                          orderDetail.ConfirmedPostalAddress?.Suburb ?? "-"
                        }
                      />

                      <ViewControl
                        label="State:"
                        value={orderDetail.ConfirmedPostalAddress?.State ?? "-"}
                      />

                      <ViewControl
                        label="Post Code:"
                        value={
                          orderDetail.ConfirmedPostalAddress?.PostCode ?? "-"
                        }
                      />

                      <ViewControl
                        label="Country:"
                        value={
                          orderDetail.ConfirmedPostalAddress?.Country ?? "-"
                        }
                      />
                    </VStack>
                  </VStack>
                </Stack>
              )}
            </Box>
          </Card>
        )}
      </PageContent>
    </>
  );
};
